import { useMemo } from "react";
import { useSiteSafe } from "@/lib/contexts/SiteContext";
import { useWorkspaceSafe } from "@/lib/contexts/WorkspaceContext";
import { useSubscription } from "@/lib/contexts/SubscriptionContext";
import type {
  CancelFlowMode,
  CancelOutcome,
  CancelSurface,
  CancellationReason,
  RecoveryOffer,
} from "@/lib/constants/cancellationReasons";
import {
  getHttpStatus,
  isNetworkFailure,
  logFeatureEvent,
  reportFeatureIssue,
  type ObservabilityScope,
} from "./shared";

export type PortalFlowResult = "cancel" | "update" | "generic";

export type BillingCancelEvent =
  | "cancel_flow_opened"
  | "cancel_reason_selected"
  | "cancel_offer_shown"
  | "cancel_offer_accepted"
  | "cancel_coupon_copied"
  | "cancel_portal_handoff"
  | "delete_request_submitted"
  | "portal_flow_unavailable";

type BillingCancelAttributes = {
  mode?: CancelFlowMode;
  surface?: CancelSurface;
  reason?: CancellationReason | null;
  offer?: RecoveryOffer | null;
  outcome?: CancelOutcome | null;
  portalFlow?: PortalFlowResult | null;
  discountApplied?: boolean | null;
};

const attributesFor = (attributes: BillingCancelAttributes = {}) => ({
  cancel_mode: attributes.mode ?? null,
  cancel_surface: attributes.surface ?? null,
  cancel_reason: attributes.reason ?? null,
  cancel_offer: attributes.offer ?? null,
  cancel_outcome: attributes.outcome ?? null,
  portal_flow: attributes.portalFlow ?? null,
  discount_applied: attributes.discountApplied ?? null,
});

/**
 * Requested a cancel/update session and got the generic portal back
 * (backend not shipped, or it degraded). Once per session — expected, never an Issue.
 */
export const logPortalFlowFallback = (
  scope: ObservabilityScope,
  attributes: { requestedFlow: "cancel" | "update" },
) =>
  logFeatureEvent({
    feature: "billing",
    event: "portal_flow_unavailable",
    level: "warn",
    message: "Billing: portal_flow_unavailable",
    scope,
    dedupKey: "portal-flow-unavailable",
    attributes: { requested_flow: attributes.requestedFlow },
  });

export const logBillingEvent = (
  event: BillingCancelEvent,
  scope: ObservabilityScope,
  attributes?: BillingCancelAttributes,
) =>
  logFeatureEvent({
    feature: "billing",
    event,
    level: event === "cancel_flow_opened" ? "warn" : "info",
    message: `Billing: ${event}`,
    scope,
    attributes: attributesFor(attributes),
  });

export const logAutoTopUpEvent = (
  event: "auto_top_up_updated" | "auto_top_up_rejected",
  scope: ObservabilityScope,
  attributes: { enabled: boolean; maxPerPeriod: number; errorCode?: string | null },
) =>
  logFeatureEvent({
    feature: "billing",
    event,
    level: event === "auto_top_up_rejected" ? "warn" : "info",
    message: `Billing: ${event}`,
    scope,
    attributes: {
      enabled: attributes.enabled,
      max_per_period: attributes.maxPerPeriod,
      error_code: attributes.errorCode ?? null,
    },
  });

export const reportBillingFailure = (
  action:
    | "open_portal"
    | "submit_delete_request"
    | "record_cancel_intent"
    | "update_auto_top_up",
  error: unknown,
  scope: ObservabilityScope,
  attributes?: BillingCancelAttributes,
) => {
  // Auth expiry and impersonation (403) are expected on cancel-intent; only a
  // rejected payload (400) or a server fault is a defect worth an Issue.
  const status = getHttpStatus(error);
  if (
    action === "record_cancel_intent" &&
    status !== null &&
    status !== 400 &&
    status < 500
  ) {
    return;
  }
  // Auto top-up 4xx are the account's state (no card, not on Agents) and
  // 503 means the price is not configured: logged by the caller, not Issues.
  if (action === "update_auto_top_up" && status !== null && (status < 500 || status === 503)) {
    return;
  }

  if (isNetworkFailure(error)) {
    logFeatureEvent({
      feature: "billing",
      event: "load_degraded",
      level: "warn",
      message: `Billing: ${action} network failure`,
      scope,
      attributes: {
        ...attributesFor(attributes),
        billing_action: action,
      },
    });
    return;
  }

  reportFeatureIssue({
    feature: "billing",
    error,
    action,
    message: `Billing: ${action} failed`,
    scope,
    context: {
      ...attributesFor(attributes),
      http_status: getHttpStatus(error),
    },
  });
};

/**
 * Hub and account-settings routes may lack SiteProvider; `property_id` is then
 * null. `plan_tier` still goes out so cancel events can be filtered by plan.
 */
export const useBillingScope = (): ObservabilityScope => {
  const site = useSiteSafe();
  const workspace = useWorkspaceSafe();
  const { currentPlan } = useSubscription();

  const propertyId =
    workspace?.selectedPropertyId || site?.currentSite?.propertyId || site?.id || null;
  const domain = site?.currentSite?.domain ?? null;
  const workspaceId = workspace?.workspace?.workspaceId ?? null;
  const planTier = currentPlan ?? null;

  return useMemo(
    () => ({ propertyId, domain, workspaceId, planTier }),
    [propertyId, domain, workspaceId, planTier],
  );
};
